import { createClient } from '@supabase/supabase-js'
import { logger } from './logger'

const log = logger('Supabase')

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Debug: log env var availability (values are NOT logged for security)
log.info(
  `Config: mode=${import.meta.env.MODE} hasUrl=${!!supabaseUrl} urlLength=${supabaseUrl?.length ?? 0} hasAnonKey=${!!supabaseAnonKey} anonKeyLength=${supabaseAnonKey?.length ?? 0}`
)

if (!supabaseUrl || !supabaseAnonKey) {
  log.warn(
    'Supabase credentials not configured. Sync features will be disabled.',
    'Make sure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in .env before running `npm run build`.'
  )
}

// Custom fetch with timeout — prevents hung requests from blocking
// the app indefinitely. Preserves any existing abort signal from the caller.
const FETCH_TIMEOUT_MS = 15000

const fetchWithTimeout: typeof fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url
  log.info('fetchWithTimeout called:', url.slice(-60))

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

  // If the caller provided a signal (e.g. from AbortController in useSync),
  // forward its abort to our controller so both can cancel the fetch.
  const callerSignal = init?.signal
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort(callerSignal.reason)
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(callerSignal.reason), { once: true })
    }
  }

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timeoutId)
  }
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    global: { fetch: fetchWithTimeout },
    auth: {
      // PKCE puts the auth code in the query string instead of tokens in the
      // URL hash. Hash fragments are easy for the PWA service worker / a
      // reload to drop; query params survive. This is also what supabase-js
      // recommends for browser apps.
      flowType: 'pkce',
      // Bypass navigator.locks which can get stuck in PWA / service-worker
      // contexts, causing getSession() to hang indefinitely on new tab open.
      // The lock's only purpose is coordinating token refresh across tabs;
      // Supabase's built-in 10s refresh-token reuse window handles the rare
      // multi-tab race gracefully, so a no-op lock is safe here.
      lock: async <R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
        return await fn()
      },
    },
  }
)

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey)

// Exposed for direct fetch() calls that bypass the Supabase client's
// internal auth middleware (which can hang on _getSession()).
export const supabaseRestUrl = supabaseUrl ? `${supabaseUrl}/rest/v1` : ''
export const supabaseApiKey = supabaseAnonKey || ''
